import React, { useContext, useMemo } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import { ResultsContext } from '../contexts/ResultsContext';
import { useTournamentAppConfiguration } from '../utils/tournamentconfig/TournamentConfiguration';
import { calculateFullTournamentRankings } from '../utils/tournamentScoring';
import './ConciergeHighlights.css';

const ConciergeHighlights = ({ historicalData }) => {
  const { theme } = useContext(ThemeContext);
  const { results } = useContext(ResultsContext);
  const tournamentAppConfiguration = useTournamentAppConfiguration();

  const highlights = useMemo(() => {
    if (!results || !results.games || !historicalData || !historicalData.games) {
      return null;
    }

    const currentRankings = calculateFullTournamentRankings(results.games, tournamentAppConfiguration);
    const pastRankings = calculateFullTournamentRankings(historicalData.games, tournamentAppConfiguration);

    const pastRankMap = new Map();
    pastRankings.forEach(p => pastRankMap.set(p.userName.toLowerCase(), p.rank));

    const movers = [];
    const newcomers = [];

    currentRankings.forEach(current => {
      const pastRank = pastRankMap.get(current.userName.toLowerCase());
      if (pastRank !== undefined) {
        const movement = pastRank - current.rank;
        if (movement > 0) {
          movers.push({
            ...current,
            pastRank,
            movement
          });
        }
      } else {
        newcomers.push(current);
      }
    });

    // Sort movers by highest movement
    const topMovers = movers
      .sort((a, b) => b.movement - a.movement)
      .slice(0, 5);

    // Sort newcomers by best current rank
    const topNewcomers = newcomers
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 5);

    return { topMovers, topNewcomers };
  }, [results, historicalData, tournamentAppConfiguration]);

  if (!highlights) return null;

  const { topMovers, topNewcomers } = highlights;

  if (topMovers.length === 0 && topNewcomers.length === 0) return null;

  return (
    <div className={`concierge-highlights-card ${theme}`}>
      <div className="highlights-header">
        <h3>Tournament Highlights</h3>
        <p className="highlights-subtitle">Since selected snapshot</p>
      </div>

      <div className="highlights-content">
        {topMovers.length > 0 && (
          <div className="highlights-section">
            <h4>🚀 Big Movers Up</h4>
            <div className="highlights-list">
              {topMovers.map(player => (
                <div key={player.userName} className="highlight-item">
                  <div className="player-info">
                    {results?.users?.[player.userName]?.avatar_path && (
                      <div 
                        className="highlight-avatar-circle" 
                        style={{ backgroundColor: results.users[player.userName].background_color_hex }}
                      >
                        <img 
                          src={results.users[player.userName].avatar_path} 
                          alt={player.userName} 
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="player-text">
                      <span className="player-name" style={{ color: results?.users?.[player.userName]?.background_color_hex }}>
                        {player.userName}
                      </span>
                      {player.initials && <span className="player-initials">({player.initials})</span>}
                    </div>
                  </div>
                  <div className="movement-stats">
                    <span className="rank-change">↑ {player.movement}</span>
                    <span className="rank-transition">#{player.pastRank} → #{player.rank}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {topNewcomers.length > 0 && (
          <div className="highlights-section">
            <h4>✨ High New Appearances</h4>
            <div className="highlights-list">
              {topNewcomers.map(player => (
                <div key={player.userName} className="highlight-item">
                  <div className="player-info">
                    {results?.users?.[player.userName]?.avatar_path && (
                      <div 
                        className="highlight-avatar-circle" 
                        style={{ backgroundColor: results.users[player.userName].background_color_hex }}
                      >
                        <img 
                          src={results.users[player.userName].avatar_path} 
                          alt={player.userName} 
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="player-text">
                      <span className="player-name" style={{ color: results?.users?.[player.userName]?.background_color_hex }}>
                        {player.userName}
                      </span>
                      {player.initials && <span className="player-initials">({player.initials})</span>}
                    </div>
                  </div>
                  <div className="rank-badge new-rank">
                    #{player.rank}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConciergeHighlights;
